import { InputState, InitialEntryState, ALPHABET } from '../types/game.types';
import { RenderManager } from '../core/RenderManager';
import { AudioManager } from '../core/AudioManager';

export class InitialEntry {
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private state: InitialEntryState;
  private onComplete: (initials: string) => void;
  private lastKeyPressed: { [key: string]: boolean } = {};
  private blinkTimer: number = 0;
  private blinkState: boolean = true;
  
  private readonly CONFIG = {
    width: 400,
    height: 300,
    charWidth: 24,
    charHeight: 32,
    spacing: 8
  };

  constructor(
    renderManager: RenderManager, 
    audioManager: AudioManager, 
    onComplete: (initials: string) => void
  ) {
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.onComplete = onComplete;
    this.state = {
      position: 0,
      selectedChar: 0,
      initials: ['A', 'A', 'A'],
      isComplete: false
    };
  }

  public update(deltaTime: number, input: InputState): void {
    if (this.state.isComplete) return;
    
    this.updateBlinking(deltaTime);
    this.handleInput(input);
  }

  private updateBlinking(deltaTime: number): void {
    this.blinkTimer += deltaTime;
    if (this.blinkTimer >= 0.5) {
      this.blinkState = !this.blinkState;
      this.blinkTimer = 0;
    }
  }

  private handleInput(input: InputState): void {
    const wasPressed = (key: string): boolean => {
      const pressed = input.keys[key] && !this.lastKeyPressed[key];
      this.lastKeyPressed[key] = input.keys[key];
      return pressed;
    };

    // Navigate up/down through characters
    if (wasPressed('ArrowUp') || wasPressed('KeyW')) {
      this.moveCharacter(-1);
      this.audioManager.playMenuMove();
    }
    
    if (wasPressed('ArrowDown') || wasPressed('KeyS')) {
      this.moveCharacter(1);
      this.audioManager.playMenuMove();
    }

    // Navigate left/right through positions
    if (wasPressed('ArrowLeft') || wasPressed('KeyA')) {
      this.movePosition(-1);
      this.audioManager.playMenuMove();
    }
    
    if (wasPressed('ArrowRight') || wasPressed('KeyD')) {
      this.movePosition(1);
      this.audioManager.playMenuMove();
    }

    // Select character and move to next position
    if (wasPressed('Space') || wasPressed('Enter')) {
      this.selectCharacter();
      this.audioManager.playMenuSelect();
    }

    // Remove character (backspace)
    if (wasPressed('Backspace')) {
      this.backspace();
      this.audioManager.playMenuMove();
    }
  }

  private moveCharacter(direction: number): void {
    this.state.selectedChar = (this.state.selectedChar + direction + ALPHABET.length) % ALPHABET.length;
    this.state.initials[this.state.position] = ALPHABET[this.state.selectedChar];
  }

  private movePosition(direction: number): void {
    if (direction > 0 && this.state.position < 2) {
      this.state.position++;
      this.state.selectedChar = ALPHABET.indexOf(this.state.initials[this.state.position]);
    } else if (direction < 0 && this.state.position > 0) {
      this.state.position--;
      this.state.selectedChar = ALPHABET.indexOf(this.state.initials[this.state.position]);
    }
  }

  private selectCharacter(): void {
    this.state.initials[this.state.position] = ALPHABET[this.state.selectedChar];
    
    if (this.state.position < 2) {
      this.state.position++;
      this.state.selectedChar = ALPHABET.indexOf(this.state.initials[this.state.position]);
    } else {
      this.complete();
    }
  }

  private backspace(): void {
    if (this.state.position > 0) {
      this.state.position--;
      this.state.selectedChar = ALPHABET.indexOf(this.state.initials[this.state.position]);
    }
  }

  private complete(): void {
    this.state.isComplete = true;
    const initials = this.state.initials.join('').trim();
    this.onComplete(initials || 'AAA');
  }

  public render(ctx: CanvasRenderingContext2D, centerX: number, centerY: number): void {
    const startX = centerX - (this.CONFIG.width / 2);
    const startY = centerY - (this.CONFIG.height / 2);

    // Draw background
    this.renderManager.fillRect(
      startX - 20, 
      startY - 20, 
      this.CONFIG.width + 40, 
      this.CONFIG.height + 40, 
      'rgba(0, 0, 0, 0.9)'
    );

    // Draw border
    this.renderManager.strokeRect(
      startX - 20, 
      startY - 20, 
      this.CONFIG.width + 40, 
      this.CONFIG.height + 40, 
      '#62D0FE', 
      2
    );

    // Title
    this.renderManager.drawTextCentered(
      'ENTER YOUR INITIALS', 
      centerX, 
      startY + 20, 
      '#62D0FE', 
      '20px "Courier New", monospace'
    );

    // Instructions
    this.renderManager.drawTextCentered(
      '↑↓ SELECT LETTER  ←→ MOVE  SPACE CONFIRM', 
      centerX, 
      startY + 50, 
      '#FFFFFF', 
      '12px "Courier New", monospace'
    );

    // Draw initial entry area
    this.renderInitialEntry(centerX, centerY);

    // Draw character selector
    this.renderCharacterSelector(centerX, centerY + 60);
  }

  private renderInitialEntry(centerX: number, centerY: number): void {
    const totalWidth = 3 * this.CONFIG.charWidth + 2 * this.CONFIG.spacing;
    const startX = centerX - (totalWidth / 2);
    
    for (let i = 0; i < 3; i++) {
      const x = startX + i * (this.CONFIG.charWidth + this.CONFIG.spacing);
      const y = centerY;
      
      // Draw character box
      const isActive = i === this.state.position;
      const boxColor = isActive ? '#62D0FE' : '#888888';
      
      this.renderManager.strokeRect(x, y, this.CONFIG.charWidth, this.CONFIG.charHeight, boxColor, 2);
      
      // Draw character
      const char = this.state.initials[i];
      const charColor = isActive && this.blinkState ? '#FFFF00' : '#FFFFFF';
      
      this.renderManager.drawTextCentered(
        char,
        x + this.CONFIG.charWidth / 2,
        y + 4,
        charColor,
        '24px "Courier New", monospace'
      );
    }
  }

  private renderCharacterSelector(centerX: number, centerY: number): void {
    const visibleChars = 7;
    const charSpacing = 32;
    const totalWidth = visibleChars * charSpacing;
    const startX = centerX - (totalWidth / 2);
    
    // Calculate range of characters to show
    const startChar = Math.max(0, Math.min(
      this.state.selectedChar - Math.floor(visibleChars / 2),
      ALPHABET.length - visibleChars
    ));
    
    for (let i = 0; i < visibleChars; i++) {
      const charIndex = startChar + i;
      if (charIndex >= ALPHABET.length) break;
      
      const x = startX + i * charSpacing;
      const char = ALPHABET[charIndex];
      const isSelected = charIndex === this.state.selectedChar;
      
      // Draw character
      let color = '#888888';
      let font = '18px "Courier New", monospace';
      
      if (isSelected) {
        color = this.blinkState ? '#FFFF00' : '#62D0FE';
        font = '24px "Courier New", monospace';
        
        // Draw selection box
        this.renderManager.strokeRect(x - 12, centerY - 2, 24, 28, '#62D0FE', 1);
      }
      
      this.renderManager.drawTextCentered(char, x, centerY, color, font);
    }
    
    // Draw scroll indicators
    if (startChar > 0) {
      this.renderManager.drawText('◀', startX - 40, centerY, '#62D0FE', '16px "Courier New", monospace');
    }
    
    if (startChar + visibleChars < ALPHABET.length) {
      this.renderManager.drawText('▶', startX + totalWidth + 20, centerY, '#62D0FE', '16px "Courier New", monospace');
    }
  }

  public reset(): void {
    this.state = {
      position: 0,
      selectedChar: 0,
      initials: ['A', 'A', 'A'],
      isComplete: false
    };
    this.lastKeyPressed = {};
    this.blinkTimer = 0;
    this.blinkState = true;
  }

  public isComplete(): boolean {
    return this.state.isComplete;
  }
}